import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { 
  Wrench, Shield, Clock, ThumbsUp, Phone, 
  CheckCircle, Star, MapPin, ArrowRight, Award 
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import './Home.css';

const Home = () => {
  const services = [
    {
      title: 'Engine Diagnostics',
      description: 'Advanced computer diagnostics to identify and resolve engine issues quickly and accurately.',
      image: 'https://images.unsplash.com/photo-1733104657949-3fefe93cde69'
    },
    {
      title: 'Brake Service',
      description: 'Complete brake inspection, repair, and replacement to keep you safe on the road.',
      image: 'https://images.unsplash.com/photo-1764869427601-eb699ac80a0c'
    },
    {
      title: 'Suspension & Steering',
      description: 'Expert repair of shocks, struts, tie rods, and steering components for smooth handling.',
      image: 'https://images.unsplash.com/photo-1770656505709-fd97236989b9'
    },
    {
      title: 'Electrical Systems',
      description: 'Alternator, starter, and battery service to keep your vehicle running reliably.',
      image: 'https://images.unsplash.com/photo-1562509048-6196fbce23de'
    },
    {
      title: 'Transmission Service',
      description: 'Transmission repair and replacement with honest assessments and quality work.',
      image: 'https://images.unsplash.com/photo-1656232976683-7b688560e427'
    },
    {
      title: 'Body & Bumper Repair',
      description: 'Front-end repair, bumper work, and collision repair to restore your vehicle.',
      image: 'https://images.unsplash.com/photo-1568644305664-5c7c2f88fa65'
    }
  ];

  const trustPoints = [
    { icon: Shield, text: 'Honest & Transparent Pricing' },
    { icon: Award, text: 'Experienced Since 2015' },
    { icon: ThumbsUp, text: 'Quality Workmanship' },
    { icon: Clock, text: 'Fast Turnaround Times' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      text: 'Moua\'s Auto Repair saved the day! My car broke down and they got me fixed the same day at a very reasonable price. The owner is honest and the staff is super friendly.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      text: 'I\'ve been taking my family\'s vehicles here for years. The service is great, prices are fair, and they always explain what needs to be done. A trustworthy shop you can count on.',
      rating: 5
    },
    {
      name: 'Jessica Martinez',
      text: 'Professional service without the dealership prices. They fixed my brake issue quickly and the price was lower than other shops quoted. Highly recommend!',
      rating: 5
    }
  ];

  const faqs = [
    {
      question: 'Do you offer same-day service?',
      answer: 'Yes! For many repairs, we can provide same-day service. Call us at (651) 772-3388 to discuss your specific needs and we\'ll do our best to accommodate your schedule.'
    },
    {
      question: 'What areas do you serve?',
      answer: 'We proudly serve St. Paul and the surrounding east metro areas including Maplewood, Oakdale, Woodbury, West St. Paul, North St. Paul, Roseville, and Little Canada.'
    },
    {
      question: 'Do you provide free estimates?',
      answer: 'Yes, we provide honest, transparent estimates for all repair work. We\'ll explain what needs to be done and give you a fair price before starting any work.'
    },
    {
      question: 'What types of vehicles do you service?',
      answer: 'We service all makes and models of cars, trucks, and SUVs. Whether you drive domestic or foreign vehicles, our experienced team can handle your repair needs.'
    }
  ];

  return (
    <div className="home">
      <Header />
      
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <img 
            src="https://images.unsplash.com/photo-1727893294198-e85137574f5b" 
            alt="Professional auto repair shop"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title fade-in-up">
              Trusted Auto Repair in <span className="text-gold">St. Paul</span>
            </h1>
            <p className="hero-subtitle fade-in-up">
              Honest service, fair pricing, and expert workmanship you can count on. 
              Your neighborhood shop for reliable automotive repair since 2015.
            </p>
            <div className="hero-actions fade-in-up">
              <a href="tel:6517723388" className="btn-primary btn-large">
                <Phone size={20} />
                Call (651) 772-3388
              </a>
              <Link to="/contact" className="btn-secondary btn-large">
                Request Service
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="trust-strip">
        <div className="container">
          <div className="trust-grid">
            {trustPoints.map((item, index) => (
              <div key={index} className="trust-item">
                <item.icon size={32} className="trust-icon" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header text-center">
            <h2>Our Services</h2>
            <p className="section-subtitle">
              Comprehensive auto repair services for all your vehicle needs
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card card">
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/services" className="btn-secondary">
              View All Services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section section-light">
        <div className="container">
          <div className="why-choose-grid">
            <div className="why-choose-content">
              <h2>Why St. Paul Drivers Trust Moua's Auto Repair</h2>
              <p className="mb-4">
                We've built our reputation on honesty, quality workmanship, and treating every customer with respect. When you bring your vehicle to Moua's, you're not just another number—you're a valued member of our community.
              </p>
              <ul className="benefits-list">
                <li>
                  <CheckCircle size={24} className="benefit-icon" />
                  <div>
                    <strong>Honest & Fair Pricing</strong>
                    <p>No surprises, no hidden fees. Just straightforward pricing you can trust.</p>
                  </div>
                </li>
                <li>
                  <CheckCircle size={24} className="benefit-icon" />
                  <div>
                    <strong>Professional Service</strong>
                    <p>Experienced technicians who take pride in quality work and customer satisfaction.</p>
                  </div>
                </li>
                <li>
                  <CheckCircle size={24} className="benefit-icon" />
                  <div>
                    <strong>Quick Turnaround</strong>
                    <p>We understand you need your vehicle. Same-day service available for many repairs.</p>
                  </div>
                </li>
                <li>
                  <CheckCircle size={24} className="benefit-icon" />
                  <div>
                    <strong>Local & Trusted</strong>
                    <p>A neighborhood shop backed by years of word-of-mouth recommendations.</p>
                  </div>
                </li>
              </ul>
              <Link to="/about" className="btn-primary mt-4">
                Learn More About Us
              </Link>
            </div>
            <div className="why-choose-image">
              <img 
                src="https://images.unsplash.com/photo-1770656505713-b0fd2f5751e6" 
                alt="Professional mechanic at work"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header text-center">
            <h2>What Our Customers Say</h2>
            <p className="section-subtitle">
              Real experiences from real St. Paul drivers
            </p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card card">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="var(--color-gold)" color="var(--color-gold)" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <p className="testimonial-author">— {testimonial.name}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/testimonials" className="btn-secondary">
              Read More Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* Local Service Area */}
      <section className="section section-light">
        <div className="container">
          <div className="local-area-content text-center">
            <MapPin size={48} className="local-icon" />
            <h2>Proudly Serving St. Paul & Surrounding Areas</h2>
            <p className="section-subtitle mb-6">
              Conveniently located at 931 Atlantic St, we're your trusted neighborhood auto repair shop serving drivers throughout the east metro.
            </p>
            <div className="service-areas">
              <span>St. Paul</span>
              <span>East St. Paul</span>
              <span>Maplewood</span>
              <span>Oakdale</span>
              <span>Woodbury</span>
              <span>West St. Paul</span>
              <span>North St. Paul</span>
              <span>Roseville</span>
              <span>Little Canada</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header text-center">
            <h2>Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Quick answers to common questions
            </p>
          </div>
          <div className="faq-container">
            <Accordion type="single" collapsible className="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="faq-question">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="text-center mt-6">
            <Link to="/faq" className="btn-secondary">
              View All FAQs
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content text-center">
            <h2>Ready to Get Your Vehicle Serviced?</h2>
            <p>
              Call us today or request a service appointment. We're here to help with honest advice and quality repairs.
            </p>
            <div className="cta-actions">
              <a href="tel:6517723388" className="btn-primary btn-large">
                <Phone size={20} />
                Call (651) 772-3388
              </a>
              <Link to="/contact" className="btn-secondary btn-large">
                Request Service Online
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
